import React, { useState } from 'react';
import { generateSearchDescription } from '../../utils/searchDescription';
import InlineScheduleEditor from './InlineScheduleEditor';

// Saved-searches tab panel.
// Pure presentational — receives data and callbacks from parent (SearchPage2).
// Renders either the inline schedule editor (when one is active) OR the
// grid of saved-search cards.
//
// Extracted from SearchPage2.js as part of the #11 refactor.
const SavedSearchesPanel = ({
  savedSearches,
  selectedSearch,
  // Inline schedule editor wiring
  editingScheduleSearch,
  scheduleEnabled,
  setScheduleEnabled,
  savingSchedule,
  onCancelScheduleEdit,
  onSaveSchedule,
  // Saved-search actions
  onUpdateSavedSearch,
  onSetDefault,
  onDelete,
  onLoad
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', notificationsEnabled: false });

  const handleStartEdit = (search) => {
    const searchId = search.id || search._id;
    setEditingId(searchId);
    setEditFormData({
      name: search.name || '',
      notificationsEnabled: search.notifications?.enabled !== false
    });
  };

  const handleSaveEdit = async (search) => {
    const searchId = search.id || search._id;
    if (!onUpdateSavedSearch || !searchId || !editFormData.name.trim()) return;

    const existingNotifications = search.notifications || {};
    const nextNotifications = {
      ...existingNotifications,
      enabled: Boolean(editFormData.notificationsEnabled)
    };

    if (nextNotifications.enabled && !nextNotifications.frequency) {
      nextNotifications.frequency = 'daily';
    }

    await onUpdateSavedSearch({
      isUpdate: true,
      id: searchId,
      name: editFormData.name.trim(),
      notifications: nextNotifications
    });

    setEditingId(null);
    setEditFormData({ name: '', notificationsEnabled: false });
  };

  const handleToggleNotifications = async (search, enabled) => {
    const searchId = search.id || search._id;
    if (!onUpdateSavedSearch || !searchId) return;

    const existingNotifications = search.notifications || {};
    const nextNotifications = {
      ...existingNotifications,
      enabled: Boolean(enabled)
    };

    if (nextNotifications.enabled && !nextNotifications.frequency) {
      nextNotifications.frequency = 'daily';
    }

    await onUpdateSavedSearch({
      isUpdate: true,
      id: searchId,
      name: (search.name || '').trim(),
      notifications: nextNotifications
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({ name: '', notificationsEnabled: false });
  };
  const schedLabel = (search) => {
    const n = search.notifications || {};
    if (n.enabled === false) return '🔕 Off';
    const f = n.frequency || 'daily';
    return `📧 ${f.charAt(0).toUpperCase() + f.slice(1)}`;
  };

  const chipsFor = (search) => {
    const c = search.criteria || {};
    const chips = [];
    if (c.gender && String(c.gender).toLowerCase() !== 'all') chips.push(c.gender);
    if (c.ageMin || c.ageMax) chips.push(`${c.ageMin || '…'}–${c.ageMax || '…'} yrs`);
    const hMin = c.heightMinFeet ? `${c.heightMinFeet}'${c.heightMinInches || 0}"` : '';
    const hMax = c.heightMaxFeet ? `${c.heightMaxFeet}'${c.heightMaxInches || 0}"` : '';
    if (hMin || hMax) chips.push(`${hMin || '…'}–${hMax || '…'}`);
    const locs = Array.isArray(c.locations) ? c.locations : (c.location ? [c.location] : []);
    if (locs.length) chips.push(locs[0] + (locs.length > 1 ? ` +${locs.length - 1}` : ''));
    if (c.state) chips.push(c.state);
    return chips;
  };

  const renderEditControls = (search) => (
    <>
      <input
        type="text"
        value={editFormData.name}
        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
        className="saved-search-name-input-inline"
        placeholder="Name"
        autoFocus
      />
      <button type="button" className="btn-save-edit" onClick={(e) => { e.preventDefault(); handleSaveEdit(search); }} title="Save">✓</button>
      <button type="button" className="btn-cancel-edit" onClick={(e) => { e.preventDefault(); handleCancelEdit(); }} title="Cancel">✕</button>
    </>
  );

  const renderActionBtns = (search, searchId, isEditing) => (
    <>
      <button type="button" onClick={(e) => { e.preventDefault(); handleStartEdit(search); }} title="Edit name and notifications" disabled={isEditing}>✏️</button>
      <button type="button" onClick={(e) => { e.preventDefault(); onDelete(searchId); }} title="Delete" disabled={isEditing}>🗑️</button>
    </>
  );
  return (
    <div className="saved-searches-tab">
      {editingScheduleSearch && (
        <InlineScheduleEditor
          search={editingScheduleSearch}
          scheduleEnabled={scheduleEnabled}
          setScheduleEnabled={setScheduleEnabled}
          savingSchedule={savingSchedule}
          onCancel={onCancelScheduleEdit}
          onSave={onSaveSchedule}
        />
      )}

      {!editingScheduleSearch && (
        <>
          {savedSearches.length === 0 ? (
            <div className="empty-saved-searches">
              <div className="empty-icon">📋</div>
              <h4>No Saved Searches Yet</h4>
              <p>Save your search criteria to quickly access them later.</p>
            </div>
          ) : (
            <>
              {(() => {
                const defaultSearch = savedSearches.find(sr => sr.isDefault) || savedSearches[0];
                const otherSearches = savedSearches.filter(sr => sr !== defaultSearch);
                const defId = defaultSearch && (defaultSearch.id || defaultSearch._id);
                const defEditing = editingId === defId;
                const defActive = selectedSearch && (selectedSearch.id === defId || selectedSearch._id === defId);
                return (
                  <>
                    {defaultSearch && (
                      <div className={`ss-hero ${defActive ? 'is-active' : ''}`}>
                        <div className="ss-hero-tag">⭐ DEFAULT SEARCH</div>
                        <div className="ss-hero-name">
                          {defEditing ? renderEditControls(defaultSearch) : (defaultSearch.name || 'Saved search')}
                        </div>
                        {chipsFor(defaultSearch).length > 0 && (
                          <div className="ss-hero-chips">
                            {chipsFor(defaultSearch).map((c, i) => <span key={i} className="ss-hero-chip">{c}</span>)}
                          </div>
                        )}
                        <div className="ss-hero-desc">
                          {defaultSearch.description || generateSearchDescription(defaultSearch.criteria, defaultSearch.minMatchScore)}
                        </div>
                        <div className="ss-hero-foot">
                          <button type="button" className="ss-hero-run" onClick={(e) => { e.preventDefault(); onLoad(defaultSearch); }} disabled={defEditing}>
                            ▶ Run now
                          </button>
                          <span className="ss-hero-meta">{schedLabel(defaultSearch)}</span>
                          <label className="ss-notif" title="Email notifications">
                            <input
                              type="checkbox"
                              checked={defEditing ? Boolean(editFormData.notificationsEnabled) : (defaultSearch.notifications?.enabled !== false)}
                              onChange={(e) => {
                                if (defEditing) { setEditFormData({ ...editFormData, notificationsEnabled: e.target.checked }); return; }
                                handleToggleNotifications(defaultSearch, e.target.checked);
                              }}
                            />
                            alerts
                          </label>
                          <div className="ss-hero-acts">
                            {renderActionBtns(defaultSearch, defId, defEditing)}
                          </div>
                        </div>
                      </div>
                    )}

                    {otherSearches.length > 0 && (
                      <div className="ss-list">
                        {otherSearches.map(search => {
                          const searchId = search.id || search._id;
                          const isEditing = editingId === searchId;
                          const isActive = selectedSearch?.id === searchId || selectedSearch?._id === searchId;
                          return (
                            <div key={searchId} className={`ss-item ${isActive ? 'is-active' : ''}`}>
                              <button
                                type="button"
                                className="ss-star"
                                onClick={(e) => { e.preventDefault(); onSetDefault(searchId, search.name, false); }}
                                title="Set as default"
                              >☆</button>
                              <div className="ss-item-name">
                                {isEditing ? renderEditControls(search) : (search.name || 'Saved search')}
                                <div className="ss-item-sub">{schedLabel(search)}</div>
                              </div>
                              <div className="ss-item-acts">
                                <button type="button" className="ss-run" onClick={(e) => { e.preventDefault(); onLoad(search); }} disabled={isEditing} title="Run this saved search" aria-label="Run this saved search">▶</button>
                                {renderActionBtns(search, searchId, isEditing)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SavedSearchesPanel;
